import { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import Badge from 'beam/dist/Badge'
import BeamIcon from 'beam/dist/BeamIcon'
import Button, { IconButton } from 'beam/dist/Button'
import Divider from 'beam/dist/Divider'
import Typography from 'beam/dist/Typography'
import withStyles from 'beam/dist/styles'
import AlertDialog from './AlertDialog'
import Alert from './Alert'
import Dropdown, { DropdownList, DropdownListItem, DropdownListSubheader, DropdownFooter } from '../NavBar/Dropdown'
import { hexToRgbaStyle } from '../../Common/Utils/index'
import { withFeatureFlags } from '../../FeatureFlags'

const styles = theme => ({
  unreadAlert: {
    fontWeight: 'bold',
  },
  alert: {
    color: hexToRgbaStyle(theme.palette.navigationTextColor, 0.7),
    '&:hover': {
      color: theme.palette.navigationTextColor,
    },
  },
  readAlert: {
    fontWeight: 'inherit',
  },
  alertButton: {
    height: theme.spacing(6),
    borderRadius: '0px',
  },
  nebulaAlertButton: {
    height: '24px',
    color: hexToRgbaStyle(theme.palette.navigationTextColor, 0.7),
    '&:hover': {
      color: theme.palette.navigationTextColor,
    },
  },
  clearButton: {
    textTransform: 'uppercase',
    width: '100%',
  },
  pageLabel: {
    display: 'inline',
  },
  dropdownRoot: {
    width: theme.spacing(40),
  },
})

class Alerts extends Component {
  static propTypes = {
    alerts: PropTypes.array.isRequired, // page of alerts
    unread: PropTypes.number.isRequired, // number of unread alerts
    total: PropTypes.number.isRequired, // total number of alerts
    page: PropTypes.number,
    pageSize: PropTypes.number.isRequired,
    error: PropTypes.string,
    onChangePage: PropTypes.func.isRequired,
    onSelectAlert: PropTypes.func.isRequired,
    onRemoveAlert: PropTypes.func.isRequired,
    onClearAll: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    canUseFeature: PropTypes.func,
  }

  static defaultProps = {
    page: 1,
    error: '',
  }

  state = {
    open: false,
    selectedAlert: null,
    hoveredAlert: null,
    anchor: null,
  }

  handleClick = event => {
    event.preventDefault()

    this.setState({
      open: true,
      anchor: event.currentTarget,
    })
  }

  handleClose = () => {
    this.setState({
      open: false,
    })
  }

  handleDialogClose = () => {
    this.setState({
      selectedAlert: null,
    })
  }

  handleDialogDelete = alert => {
    this.props.onRemoveAlert(alert)
    this.handleDialogClose()
  }

  showAlert = alert => {
    const { onSelectAlert } = this.props

    this.setState({
      selectedAlert: alert,
    })
    onSelectAlert(alert)
  }

  goToFirstPage = () => {
    this.props.onChangePage(1)
  }

  goToPrevPage = () => {
    const { page } = this.props
    if (page > 1) {
      this.props.onChangePage(page - 1)
    }
  }

  goToNextPage = () => {
    const { page } = this.props
    if (page < this.getMaxPages()) {
      this.props.onChangePage(page + 1)
    }
  }

  goToLastPage = () => {
    this.props.onChangePage(this.getMaxPages())
  }

  getMaxPages = () => {
    return Math.ceil(this.props.total / this.props.pageSize)
  }

  setHoverState = alert => {
    this.setState({
      hoveredAlert: alert,
    })
  }

  clearHoverState = () => {
    this.setState({
      hoveredAlert: null,
    })
  }

  renderAlerts = () => {
    const { alerts, t } = this.props

    return alerts.length > 0 ? (
      this.renderAlertList(alerts)
    ) : (
      <DropdownListItem style={{ textAlign: 'center' }}>
        <Typography variant="subtitle1" gutterBottom align="center">
          {t('noAlerts')}
        </Typography>
      </DropdownListItem>
    )
  }

  renderAlertList = alerts =>
    alerts.reduce((acc, child, index) => {
      const alert = this.renderAlert(child)
      acc.push(alert)
      if (index < alerts.length - 1) {
        acc.push(<Divider key={`Divider-${index}`} />)
      }

      return acc
    }, [])

  renderAlert = alert =>
    alert && (
      <Alert
        key={alert.id}
        text={alert.summary}
        timestamp={alert.timestamp}
        unread={alert.state === 'u'}
        onMouseEnter={this.setHoverState.bind(this, alert)}
        onMouseLeave={this.clearHoverState}
        onClick={this.showAlert.bind(this, alert)}
        onDelete={this.props.onRemoveAlert.bind(this, alert)}
        showDeleteIcon={this.state.hoveredAlert === alert}
      />
    )

  renderFooter = () => {
    const { t, page } = this.props
    const lastPage = this.getMaxPages()

    return (
      <DropdownFooter style={{ textAlign: 'center' }}>
        <div>
          <IconButton onClick={this.goToFirstPage} disabled={page === 1}>
            <BeamIcon>ChevronDoubleLeft</BeamIcon>
          </IconButton>
          <IconButton onClick={this.goToPrevPage} disabled={page === 1}>
            <BeamIcon>ChevronLeft</BeamIcon>
          </IconButton>
          <Typography className={this.props.classes.pageLabel} variant="caption">
            {page}
          </Typography>
          <IconButton onClick={this.goToNextPage} disabled={page === lastPage}>
            <BeamIcon>ChevronRight</BeamIcon>
          </IconButton>
          <IconButton onClick={this.goToLastPage} disabled={page === lastPage}>
            <BeamIcon>ChevronDoubleRight</BeamIcon>
          </IconButton>
        </div>
        <div>
          <Button
            className={this.props.classes.clearButton}
            variant="text"
            color="primary"
            onClick={this.props.onClearAll}
          >
            {t('clearSelected')}
          </Button>
        </div>
      </DropdownFooter>
    )
  }
  render() {
    const { unread, error, classes, t } = this.props
    const { open, selectedAlert, anchor } = this.state

    return (
      <Fragment>
        <IconButton className={classes.alertButton} onClick={this.handleClick} auto-qa-id={'Alerts-button'} data-testid={'Alerts-button'}>
          <Badge badgeContent={error ? '!' : unread > 99 ? '99+' : unread} color="secondary">
            <BeamIcon className={classes.alert}>Bell</BeamIcon>
          </Badge>
        </IconButton>
        <Dropdown open={open} anchor={anchor} onClose={this.handleClose} classes={{ root: classes.dropdownRoot }}>
          <DropdownList>
            <DropdownListSubheader>{t('alerts')}</DropdownListSubheader>
            {this.renderAlerts()}
            {this.renderFooter()}
          </DropdownList>
        </Dropdown>
        <AlertDialog
          alert={selectedAlert}
          onDelete={this.handleDialogDelete.bind(this, selectedAlert)}
          onClose={this.handleDialogClose}
        />
      </Fragment>
    )
  }
}

const alertsWithFeatureFlags = withFeatureFlags(Alerts)

export { alertsWithFeatureFlags as UnstyledAlerts }
export default withFeatureFlags(withTranslation()(withStyles(styles)(Alerts)))
